import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router'
import { Button } from '../ui'
import { Permissions } from '../onboarding/screens/Permissions'
import {
  acceptInvitation, createWorkspace, declineInvitation, fetchInvitations, fetchMe,
  fetchWorkspaces, selectWorkspace, signOut, startConsent, startMicrosoftLink,
} from '../lib/api'
import type { PendingInvitation, WorkspaceChoice } from '../lib/api'
import type { Me } from '../lib/api'
import { linkedOutcomeMessage } from './linkedOutcome'
import '../onboarding/onboarding.css'
import './connect.css'
import './magic-link.css'

type Phase =
  | 'checking'
  | 'ready'
  | 'starting'
  | 'noSession'
  | 'needsMicrosoftSignIn'
  | 'notPermitted'
  | 'workEmailRequired'
  | 'unavailable'

/**
 * Where a signed-in customer starts admin consent.
 *
 * This route did not exist, which made the rest of the consent design unreachable: the API
 * redirects failures here, the SPA catch-all sent `/connect` to sign-in, and nothing in a
 * production build ever called `startConsent()`. A person could sign in with Microsoft and then
 * find that connecting a tenant was, in the previous screen's own words, "not built".
 *
 * The screen itself is the approved Permissions screen from the onboarding flow — the trust
 * screen designed to sit before Microsoft's consent page, not after it. Nothing new is drawn
 * here; this component supplies the session, the real endpoints and the failure states.
 */
export function ConnectTenant() {
  const navigate = useNavigate()
  const [params] = useSearchParams()

  const [phase, setPhase] = useState<Phase>('checking')
  const [switchingAddress, setSwitchingAddress] = useState(false)
  const [switchAddressFailed, setSwitchAddressFailed] = useState(false)
  const [me, setMe] = useState<Me | null>(null)

  /*
   * INVITATIONS COME BEFORE A WORKSPACE, and this screen is where that has to be true.
   *
   * Every landing for a signed-in person with no workspace pointed here, and the only button here
   * created a workspace before starting consent. An invitee who followed it became the owner of
   * an empty workspace, their session named it, and from then on no session of theirs could accept
   * the invitation -- the server refuses a placed session, correctly. The product's own path
   * walked people into a dead end. Found by the independent review, which named the state:
   * the bootstrap-placed invitee.
   *
   * So pending invitations are fetched beside the session, shown first, and accepting one is the
   * primary action. The server refuses to create a workspace while any are pending, so this is
   * the courteous half of a rule the database enforces regardless.
   */
  const [invitations, setInvitations] = useState<PendingInvitation[] | null>(null)
  const [workspaces, setWorkspaces] = useState<WorkspaceChoice[]>([])
  const [entering, setEntering] = useState<string | null>(null)
  const [accepting, setAccepting] = useState<string | null>(null)
  // Declining is its own action with its own label. It used to borrow `accepting`, so pressing
  // Decline made the button beside it say "Opening…".
  const [declining, setDeclining] = useState<string | null>(null)
  // Which invitation is asking "are you sure". Declining cannot be undone from here -- an owner
  // has to invite again -- and it sat one click away, next to the primary button.
  const [confirmingDecline, setConfirmingDecline] = useState<string | null>(null)
  const [inviteNote, setInviteNote] = useState<string | null>(null)
  const startingRef = useRef(false)

  // What the callback said on its way back here, if it came back here. `reported_error` is
  // deliberately not treated as a decline -- see the callback: whoever holds the state can
  // navigate a browser here with any error they like.
  //
  // READ ONCE, then taken out of the URL. It is a message about a navigation that has already
  // happened, and leaving it in the address bar makes it a message about right now: reload, come
  // back later, follow a bookmark, and a red banner is still asserting that a connection failed
  // -- long after one succeeded. Held in state so this render still shows it, and removed from
  // history so the next one does not.
  //
  // replaceState rather than a redirect, so the back button is not consumed by tidying up.
  const [outcome] = useState(() => params.get('outcome'))

  // What the "connect Microsoft sign-in" round trip said on its way back, when it was started from this
  // screen (`?linked=ok|failed|owned_elsewhere|signed_out`). Read once and taken out of the URL, exactly like
  // `outcome` above and for the same reason: it describes a navigation that has already happened.
  const [linked] = useState(() => params.get('linked'))
  const [linking, setLinking] = useState(false)
  const [linkNote, setLinkNote] = useState<string | null>(null)

  useEffect(() => {
    if (!params.get('outcome') && !params.get('linked')) return

    const url = new URL(window.location.href)
    url.searchParams.delete('outcome')
    url.searchParams.delete('linked')
    window.history.replaceState({}, '', url.toString())
  }, [params])

  useEffect(() => {
    let cancelled = false

    void (async () => {
      const result = await fetchMe()
      if (cancelled) return

      // A transient failure is not a signed-out visitor. Collapsing the two would send someone
      // with a perfectly good session back to sign in because one request lost a race.
      if (!result.ok) {
        setPhase(result.status === 401 ? 'noSession' : 'unavailable')
        return
      }

      if (!result.data) {
        setPhase('noSession')
        return
      }

      setMe(result.data)

      // Only somebody with no workspace can be a bootstrap-placed invitee; for everyone else
      // the question does not arise and the fetch is skipped.
      if (!result.data.workspaceId) {
        const [invited, mine] = await Promise.all([fetchInvitations(), fetchWorkspaces()])
        if (cancelled) return
        setInvitations(invited.ok ? invited.data.invitations : [])
        setWorkspaces(mine.ok ? mine.data.workspaces : [])
      } else {
        setInvitations([])
      }

      setPhase('ready')
    })()

    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (phase !== 'noSession') return
    const timer = setTimeout(() => navigate('/auth/sign-in'), 1200)
    return () => clearTimeout(timer)
  }, [phase, navigate])

  // BACK FROM MICROSOFT. The guard above deliberately stays set once the browser is on its way to
  // Microsoft's consent page. But pressing Back there (wrong account, second thoughts) restores this
  // page from the back/forward cache with its state intact -- guard set, phase 'starting' -- and the
  // only button on it stayed disabled, reading "Taking you to Microsoft…", until a reload.
  useEffect(() => {
    function restored(event: PageTransitionEvent) {
      if (!event.persisted) return
      startingRef.current = false
      setPhase((current) => (current === 'starting' ? 'ready' : current))
      // The same for "Connect Microsoft sign-in": Back from Microsoft's page must give the button back.
      setLinking(false)
    }
    window.addEventListener('pageshow', restored)
    return () => window.removeEventListener('pageshow', restored)
  }, [])

  const connect = useCallback(async () => {
    // A ref, not `phase`: this callback is keyed on `me`, so the phase it sees can be stale, and
    // two presses inside one frame would each create a workspace attempt and a consent request.
    if (startingRef.current) return
    startingRef.current = true
    setPhase('starting')

    // A session with no workspace cannot start consent -- the endpoint reads the workspace from
    // the session and refuses without one. So the workspace is created first, here, rather than
    // leaving the person on a screen whose only button returns 401 forever.
    if (!me?.workspaceId) {
      const created = await createWorkspace(me?.name ?? null)

      if (!created.ok) {
        // 409 invitations_pending: the server will not make a workspace for somebody who has
        // been invited to one. Show the invitation rather than a generic failure.
        if (created.status === 409) {
          const invited = await fetchInvitations()
          setInvitations(invited.ok ? invited.data.invitations : [])
          startingRef.current = false
          setPhase('ready')
          return
        }

        startingRef.current = false
        setPhase(created.status === 401 ? 'noSession'
          : created.status === 403 && created.code === 'work_email_required' ? 'workEmailRequired'
          : 'unavailable')
        return
      }
    }

    const result = await startConsent()

    if (result.ok) {
      // Leaves the SPA entirely. Microsoft's consent page is Microsoft's page, and this is a
      // full navigation rather than anything framed or fetched -- a security decision the
      // customer makes has to happen at a URL they can see and check. The guard stays set: the
      // page is going away.
      window.location.assign(result.data.consentUrl)
      return
    }

    startingRef.current = false
    setPhase(
      result.status === 401
        ? 'noSession'
        : result.status === 403
          ? 'notPermitted'
          : result.status === 409
            ? 'needsMicrosoftSignIn'
            : 'unavailable',
    )
  }, [me])

  /*
   * Starts attaching the Microsoft account from HERE, instead of sending the person to Settings to find the
   * same button. The same endpoint, the same deliberate credential prompt at Microsoft; the only difference
   * is that it asks to come back to this screen, where the next step is waiting.
   */
  const connectMicrosoft = async () => {
    if (linking) return

    setLinking(true)
    setLinkNote(null)

    const result = await startMicrosoftLink('connect')

    if (result.ok && result.data.authorizeUrl) {
      // Leaves the SPA entirely, like consent: Microsoft's page is Microsoft's page.
      window.location.assign(result.data.authorizeUrl)
      return
    }

    setLinking(false)
    setLinkNote(
      result.ok === false && result.status === 401
        ? 'Your sign-in has ended. Sign in again, then try this once more.'
        : 'That could not be started just now. Your account is unchanged, and you can try again.',
    )
  }

  if (phase === 'checking') {
    return (
      <main className="magic-link rise-in">
        <div className="label-mono">Defenssive AI Security Manager</div>
        <h2>One moment…</h2>
        <p className="text-muted">Checking your session.</p>
      </main>
    )
  }

  if (phase === 'noSession') {
    return (
      <main className="magic-link rise-in">
        <div className="label-mono">Defenssive AI Security Manager</div>
        <h2>Please sign in first</h2>
        <p className="text-muted">Taking you to sign in.</p>
      </main>
    )
  }

  if (phase === 'needsMicrosoftSignIn') {
    /*
     * THIS SCREEN USED TO SEND PEOPLE SOMEWHERE THAT NOW REFUSES THEM.
     *
     * Its button went to Microsoft sign-in, which was the right remedy when an Entra assertion
     * could still create or reach an account. Migration 0104 ended that: only possession of the
     * mailbox creates an account, so signing in with Microsoft as somebody this product has never
     * seen is refused, and the person arrives back here having gone in a circle.
     *
     * The remedy is attaching the Microsoft account to the session they are already holding, which
     * is the one thing only they can do. The button starts that link from here (the same flow as
     * Settings), pre-filled with their address, and Microsoft returns them to this screen.
     */
    return (
      <main className="magic-link rise-in">
        <div className="label-mono">Defenssive AI Security Manager</div>
        <h2>Connect your Microsoft account first</h2>
        <p className="text-muted">
          Connecting a tenant requires the Microsoft account that administers it, so we know which
          directory to expect — and we can only use one you have attached to this account. Nothing
          in your tenant has changed.
        </p>
        <p className="text-muted">
          Microsoft will ask you to sign in with the account that administers your tenant — even if you
          already are signed in — and then brings you back here to continue.
        </p>
        <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
          <Button variant="primary" onClick={() => void connectMicrosoft()} disabled={linking}>
            {linking ? 'Taking you to Microsoft…' : 'Connect Microsoft sign-in'}
          </Button>
          <Button variant="ghost" onClick={() => navigate('/settings/account')}>Go to Settings</Button>
        </div>
        {linkNote ? <p className="text-muted" role="alert">{linkNote}</p> : null}
      </main>
    )
  }

  if (phase === 'workEmailRequired') {
    return (
      <main className="magic-link rise-in">
        <div className="label-mono">Defenssive AI Security Manager</div>
        <h2>Use a work email for a new workspace</h2>
        <p className="text-muted">
          Known personal and temporary email domains cannot create new workspaces. You can still
          use your existing account or accept an invitation from a workspace owner. Nothing in
          your Microsoft tenant has changed.
        </p>
        <Button disabled={switchingAddress} onClick={async () => {
          setSwitchingAddress(true)
          setSwitchAddressFailed(false)
          const succeeded = await signOut()
          setSwitchAddressFailed(!succeeded)
          setSwitchingAddress(false)
          if (succeeded) navigate('/auth/sign-in?signed_out=1')
        }}>{switchingAddress ? 'Signing out…' : 'Sign out and use a work email'}</Button>
        <Button variant="ghost" disabled={switchingAddress} onClick={() => window.location.reload()}>Check for invitations</Button>
        {switchAddressFailed ? <p role="alert">We could not confirm sign-out. Try again before using another address.</p> : null}
      </main>
    )
  }

  if (phase === 'notPermitted') {
    return (
      <main className="magic-link rise-in">
        <div className="label-mono">Defenssive AI Security Manager</div>
        <h2>Someone else needs to do this</h2>
        {/*
          A workspace role, not an Entra one. This is not the "you are not a Global Administrator"
          case -- that one happens on Microsoft's page and is answered there -- so it must not say
          so, or the person goes looking for the wrong permission.
        */}
        <p className="text-muted">
          Connecting a Microsoft tenant is an owner or admin action for this workspace. Ask an
          owner or admin to connect it. Nothing in your tenant has changed.
        </p>
        {/* This screen had no way forward or back at all: a member who reached it (from an old
            link, or the button this replaced) could only use the browser's Back button. */}
        {me?.workspaceId ? (
          <Button onClick={() => navigate('/dashboard')}>Go to your dashboard</Button>
        ) : null}
      </main>
    )
  }

  if (phase === 'unavailable') {
    return (
      <main className="magic-link rise-in">
        <div className="label-mono">Defenssive AI Security Manager</div>
        <h2>We could not start that</h2>
        <p className="text-muted">
          Something on our side is not ready. Nothing in your Microsoft tenant has been touched.
        </p>
        <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
          <Button onClick={() => window.location.reload()}>Try again</Button>
          {me?.workspaceId ? (
            <Button variant="ghost" onClick={() => navigate('/dashboard')}>Go to your dashboard</Button>
          ) : null}
        </div>
      </main>
    )
  }

  // ALREADY BELONGS TO ONE OR MORE, and the session named none -- several memberships, or a
  // pending invitation, so sign-in did not auto-place them (migration 0114). Let them enter one.
  // ── One screen for a session that names no workspace ──────────────────────────────────
  //
  // BOTH the workspaces this person may enter AND the invitations waiting, on the same screen.
  // The first version returned the workspace chooser and only MENTIONED invitations in a line of
  // prose -- so an existing member with a pending invitation saw the chooser and had no way to
  // accept the invitation: the accept action was in a branch this early return skipped, and
  // Settings refuses acceptance from a placed session. The dead end reappeared one route over.
  // Found by codex. Now every action lives here while the session is unplaced.
  const enter = async (choice: WorkspaceChoice) => {
    if (entering || accepting || declining) return
    setEntering(choice.workspaceId)
    setInviteNote(null)

    const result = await selectWorkspace(choice.workspaceId)

    if (result.ok && result.data.attached) {
      window.location.assign('/')
      return
    }

    setEntering(null)
    setInviteNote(
      result.ok === false && result.status === 401
        ? 'Your sign-in has ended. Sign in again to choose a workspace.'
        : 'That workspace could not be opened just now. Try again in a moment.',
    )
  }

  const accept = async (invitation: PendingInvitation) => {
    if (entering || accepting || declining) return
    setAccepting(invitation.workspaceId)
    setInviteNote(null)

    const result = await acceptInvitation(invitation.workspaceId)

    if (result.ok && result.data.accepted) {
      // Accepted AND attached in one write since migration 0110; the session now names it.
      window.location.assign('/')
      return
    }

    setAccepting(null)
    setInviteNote(
      result.ok
        ? 'That invitation is no longer open. Reload to see what is still waiting for you.'
        : result.status === 401
          ? 'Your sign-in has ended. Sign in again and this will be shown first.'
          : 'That did not go through, and nothing was accepted. Try again in a moment.',
    )
  }

  const declineOne = async (invitation: PendingInvitation) => {
    if (entering || accepting || declining) return
    setDeclining(invitation.workspaceId)
    setConfirmingDecline(null)
    setInviteNote(null)

    const result = await declineInvitation(invitation.workspaceId)
    setDeclining(null)

    if (result.ok && result.data.declined) {
      setInvitations(current => (current ?? []).filter(i => i.workspaceId !== invitation.workspaceId))
      setInviteNote(`You declined the invitation to ${invitation.workspaceName}.`)
      return
    }

    setInviteNote('That did not go through. The invitation is unchanged; try again in a moment.')
  }

  const hasInvites = !!invitations && invitations.length > 0

  if (workspaces.length > 0 || hasInvites) {
    const busyId = entering ?? accepting ?? declining

    return (
      <main className="connect-root rise-in">
        {workspaces.length > 0 ? (
          <>
            <h2>Choose a workspace</h2>
            <p className="text-muted">
              You belong to more than one, so this sign-in did not pick for you. Open one to work
              in it.
            </p>
            <ul className="connect-invitations">
              {workspaces.map(choice => (
                <li key={choice.workspaceId}>
                  <span>{choice.workspaceName}</span>
                  <Button
                    onClick={() => void enter(choice)}
                    disabled={busyId === choice.workspaceId}
                  >
                    {entering === choice.workspaceId ? 'Opening…' : 'Open'}
                  </Button>
                </li>
              ))}
            </ul>
          </>
        ) : null}

        {hasInvites ? (
          <>
            <h2>{workspaces.length > 0 ? 'Invitations waiting' : 'You have been invited to a workspace'}</h2>
            <p className="text-muted">
              Accepting opens it for this sign-in. Nothing needs connecting — the workspace already
              has its Microsoft tenant.
            </p>
            <ul className="connect-invitations">
              {invitations!.map(invitation => (
                <li key={invitation.workspaceId}>
                  <span>{invitation.workspaceName}</span>
                  {confirmingDecline === invitation.workspaceId ? (
                    <>
                      <span className="connect-confirm" role="status">
                        Decline? An owner would need to invite you again.
                      </span>
                      <Button variant="ghost" onClick={() => void declineOne(invitation)}>
                        Yes, decline
                      </Button>
                      <Button variant="ghost" onClick={() => setConfirmingDecline(null)}>
                        Keep it
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button
                        onClick={() => void accept(invitation)}
                        disabled={busyId === invitation.workspaceId}
                      >
                        {accepting === invitation.workspaceId ? 'Opening…' : 'Accept and open'}
                      </Button>
                      <Button
                        variant="ghost"
                        onClick={() => setConfirmingDecline(invitation.workspaceId)}
                        disabled={busyId === invitation.workspaceId}
                      >
                        {declining === invitation.workspaceId ? 'Declining…' : 'Decline'}
                      </Button>
                    </>
                  )}
                </li>
              ))}
            </ul>
          </>
        ) : null}

        {/* Announced: a refusal here was a plain paragraph that appeared without a word. */}
        {inviteNote ? <p className="text-muted" role="alert">{inviteNote}</p> : null}
        <p className="text-muted">
          Setting up a different organisation instead is done from Settings once you are in.
        </p>
      </main>
    )
  }

  return (
    <main className="connect-root rise-in">
      {/*
        Every outcome the rest of the flow can send here, named.

        This used to be `refused` and "everything else is an error", which meant a SUCCESSFUL
        connection -- outcome=connected -- rendered "Microsoft reported a problem on the way
        back". The one screen that had to be unambiguous said the opposite of what happened.

        And the refused copy then made a claim of its own that we cannot support: "nothing in
        your Microsoft tenant has changed". We do not know that. By the time Microsoft redirects
        back from an admin-consent screen the administrator may well have approved, and the grant
        is theirs and already made -- it happened exactly this way when a new permission was added
        to the registration and consented from the Azure portal. A security product telling a
        customer that nothing changed in their tenant, when something did, is worse than telling
        them nothing.
      */}
      {linked ? (
        <div className={linked === 'ok' ? 'sign-in-notice is-info' : 'sign-in-notice'} role="status">
          {linkedOutcomeMessage(linked)}
          {linked === 'ok' ? ' You can now connect your Microsoft 365 tenant below.' : null}
        </div>
      ) : null}

      {/*
        TRY AGAIN WHERE THE MESSAGE SAYS SO. A link that failed or was refused leaves no Microsoft account
        attached, so "Continue to Microsoft" below would only bring back the stop screen and its button --
        two presses for one retry. Not after signed_out: whoever is signed in now is not who started it.
      */}
      {linked && linked !== 'ok' && linked !== 'signed_out' ? (
        <div style={{ display: 'grid', gap: 'var(--space-2)', marginBottom: 'var(--space-4)' }}>
          <div>
            <Button variant="primary" onClick={() => void connectMicrosoft()} disabled={linking}>
              {linking ? 'Taking you to Microsoft…' : 'Connect Microsoft sign-in'}
            </Button>
          </div>
          {linkNote ? <p className="text-muted" role="alert">{linkNote}</p> : null}
        </div>
      ) : null}

      {outcome ? (
        <div className="connect-outcome" role="status">
          {outcome === 'refused' ? (
            <>
              <strong>We did not complete that connection.</strong> Nothing was attached on
              our side. If you approved the permissions in Microsoft, that approval stands — this
              only means we could not match it to a request you started here, which is what a
              reloaded page or a consent begun from the Azure portal looks like. Starting again
              below is safe.
            </>
          ) : outcome === 'declined' ? (
            <>
              <strong>You declined that connection.</strong> Nothing was attached. You can start
              again below whenever you are ready.
            </>
          ) : outcome === 'tenant_mismatch' ? (
            <>
              <strong>That directory was not the one this sign-in expected.</strong> Nothing was
              attached. Sign in with an account in the tenant you want to connect, then try again.
            </>
          ) : outcome === 'connected_elsewhere' ? (
            <>
              {/*
                Worded to match ConsentReview's 409, because these are the same event reached two
                ways and a person who sees both should not think two different things happened.
                Says nothing about the other workspace, its owner, or whether anyone was reachable
                -- see ADR 0014 for why the asymmetry is deliberate.
              */}
              <strong>Someone at your organisation has already connected this directory.</strong>{' '}
              Ask them to add you to it — one account per organisation is deliberate, so that
              everyone sees the same findings.
            </>
          ) : outcome === 'reported_error' ? (
            <>
              {/*
                The callback carried an error parameter. RECORDED and not believed -- anyone
                holding the state can navigate here with any error they like -- so this says what
                we saw rather than what happened.
              */}
              <strong>Microsoft reported a problem on the way back.</strong> We have noted it.
              Nothing was attached, and you can try again below.
            </>
          ) : (
            <>
              <strong>That connection did not complete.</strong> Nothing was attached, and you can
              try again below.
            </>
          )}
        </div>
      ) : null}

      <Permissions busy={phase === 'starting'} onContinue={() => void connect()} />

      {phase === 'starting' ? (
        <p className="text-muted connect-busy" role="status">
          Taking you to Microsoft…
        </p>
      ) : null}
    </main>
  )
}
